import { Icon } from "@opendash/icons";
import {
  Descriptions,
  Drawer,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { ExportButton } from "../../../components/ExportButton";
import { ImageGallery } from "../../../components/ImageGallery";
import { getUsername } from "../../../helper/getUsername";
import {
  SIV_GoodsReceiptProcess,
  SIV_GoodsReceiptProcessPosition,
} from "../../../parsegenerated";
import {
  useGoodsReceiptProcessPositionTexts,
  useGoodsReceiptProcessPositions,
} from "../hooks";

type Props = {
  goodsReceiptProcess: SIV_GoodsReceiptProcess | null;
  open: boolean;
  onClose: () => void;
};

const PositionTexts = observer(
  ({ position }: { position: SIV_GoodsReceiptProcessPosition }) => {
    const { texts, loading } = useGoodsReceiptProcessPositionTexts(position);

    if (loading) return <Spin size="small" />;
    if (texts.length === 0)
      return (
        <Typography.Text type="secondary">
          Keine Texte vorhanden
        </Typography.Text>
      );

    const renderField = (field: "texts" | "textsF") => (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {texts.map((rec) => {
          const items = (rec[field] || []) as any[];
          if (items.length === 0) return null;
          return (
            <div key={rec.id} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <Typography.Text type="secondary" style={{ minWidth: 32, paddingTop: 2 }}>
                {rec.no}
              </Typography.Text>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {items.map((t: any, i: number) => (
                  <Tag key={i}>{typeof t === "string" ? t : JSON.stringify(t)}</Tag>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );

    return (
      <Tabs
        size="small"
        style={{ margin: "4px 0" }}
        items={[
          { key: "texts", label: "Texte", children: renderField("texts") },
          { key: "textsF", label: "Texte (F)", children: renderField("textsF") },
        ]}
      />
    );
  },
);

export const GoodsReceiptProcessPositionsDrawer = observer((props: Props) => {
  const { goodsReceiptProcess, open, onClose } = props;
  const { positions, loading } =
    useGoodsReceiptProcessPositions(goodsReceiptProcess);
  const imageDirectory = goodsReceiptProcess?.directory;

  const columns: ColumnType<SIV_GoodsReceiptProcessPosition>[] = [
    {
      title: "Pos.",
      dataIndex: "positionNo",
      key: "positionNo",
      width: 60,
    },
    {
      title: "Auftrags-Nr.",
      dataIndex: "orderNumber",
      key: "orderNumber",
      width: 120,
      render: (val: string) => val || "-",
    },
    {
      title: "Auftragspos.",
      dataIndex: "orderPosition",
      key: "orderPosition",
      width: 100,
      render: (val: string) => val || "-",
    },
    {
      title: "Produkt",
      dataIndex: "productNo",
      key: "productNo",
      width: 110,
      render: (val: string) => val || "-",
    },
    {
      title: "Teil",
      dataIndex: "partNo",
      key: "partNo",
      width: 110,
      render: (val: string) => val || "-",
    },
    {
      title: "Geplant",
      dataIndex: "plannedQuantity",
      key: "plannedQuantity",
      width: 90,
      render: (val: number) => val ?? "-",
    },
    {
      title: "LS-Menge",
      dataIndex: "deliveryNoteQuantity",
      key: "deliveryNoteQuantity",
      width: 90,
      render: (val: number) => val ?? "-",
    },
    {
      title: "Menge",
      dataIndex: "quantity",
      key: "quantity",
      width: 80,
      render: (val: number) => val ?? "-",
    },
    {
      title: "Erhalten",
      dataIndex: "receivedQuantity",
      key: "receivedQuantity",
      width: 80,
      render: (val: number) => val ?? "-",
    },
    {
      title: "Einheit",
      dataIndex: "unit",
      key: "unit",
      width: 70,
      render: (val: string) => val || "-",
    },
    {
      title: "Gewicht",
      dataIndex: "weight",
      key: "weight",
      width: 90,
      render: (val: number) => (val ? `${val} kg` : "-"),
    },
    {
      title: "Abmessungen",
      dataIndex: "dimensions",
      key: "dimensions",
      width: 110,
      render: (val: string) => val || "-",
    },
    {
      title: "Herkunft",
      dataIndex: "countryOfOrigin",
      key: "countryOfOrigin",
      width: 90,
      render: (val: string) => val || "-",
    },
    {
      title: "Lagerort",
      dataIndex: "storageLocation",
      key: "storageLocation",
      width: 120,
      render: (val: string) =>
        val ? (
          <Tag color="blue">
            <Icon icon="fa:warehouse" /> {val}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Hinweis",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
      render: (val: string) => val || "-",
    },
    {
      title: "Geändert",
      key: "modified",
      width: 150,
      render: (_, record) =>
        record.changedAt ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(record.changedAt).format("DD.MM.YYYY")}
            {record.changedBy ? ` von ${getUsername(record.changedBy)}` : ""}
          </Typography.Text>
        ) : (
          "-"
        ),
    },
  ];

  const exportData = positions.map((pos: SIV_GoodsReceiptProcessPosition) => ({
    "Pos.": pos.positionNo,
    "Auftrags-Nr.": pos.orderNumber || "",
    Auftragspos: pos.orderPosition || "",
    Produkt: pos.productNo || "",
    Teil: pos.partNo || "",
    Geplant: pos.plannedQuantity ?? "",
    "LS-Menge": pos.deliveryNoteQuantity ?? "",
    Menge: pos.quantity ?? "",
    Erhalten: pos.receivedQuantity ?? "",
    Einheit: pos.unit || "",
    "Gewicht (kg)": pos.weight ?? "",
    Abmessungen: pos.dimensions || "",
    Herkunft: pos.countryOfOrigin || "",
    Lagerort: pos.storageLocation || "",
    Hinweis: pos.notes || "",
    "Geändert am": pos.changedAt
      ? dayjs(pos.changedAt).format("DD.MM.YYYY")
      : "",
    "Geändert von": pos.changedBy ? getUsername(pos.changedBy) : "",
  }));

  return (
    <Drawer
      title={
        <>
          <Icon icon="fa:truck" /> Wareneingang {goodsReceiptProcess?.no} –
          Positionen
        </>
      }
      open={open}
      onClose={onClose}
      width={1100}
      extra={
        <Space>
          {positions.length > 0 && (
            <ExportButton
              data={exportData}
              filename={`Wareneingang_${goodsReceiptProcess?.no || "export"}_Positionen`}
            />
          )}
        </Space>
      }
    >
      {goodsReceiptProcess && (
        <>
          <Descriptions size="small" column={3} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Datum">
              {goodsReceiptProcess.date
                ? dayjs(goodsReceiptProcess.date).format("DD.MM.YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Lagerort">
              {goodsReceiptProcess.storageLocation ? (
                <Tag color="blue">{goodsReceiptProcess.storageLocation}</Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Gewicht">
              {goodsReceiptProcess.weight
                ? `${goodsReceiptProcess.weight} kg`
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Lieferschein">
              {goodsReceiptProcess.deliveryNote ? (
                <Tag color="green">{goodsReceiptProcess.deliveryNote.no}</Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Lieferant">
              {goodsReceiptProcess.supplier || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Spediteur">
              {goodsReceiptProcess.transportationProvider || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Xcellize-Nr.">
              {goodsReceiptProcess.xcellizeNo || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Druckdatum">
              {goodsReceiptProcess.printDate
                ? dayjs(goodsReceiptProcess.printDate).format("DD.MM.YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Kommentar" span={3}>
              {goodsReceiptProcess.comment || "-"}
            </Descriptions.Item>
          </Descriptions>

          <ImageGallery
            directory={imageDirectory}
            title="Bilder"
            height={170}
          />

          {loading ? (
            <Spin />
          ) : (
            <Table
              columns={columns}
              dataSource={positions}
              rowKey={(record) => record.id!}
              size="small"
              pagination={false}
              scroll={{ x: "max-content" }}
              expandable={{
                expandedRowRender: (record) => (
                  <PositionTexts position={record} />
                ),
                rowExpandable: () => true,
              }}
            />
          )}
        </>
      )}
    </Drawer>
  );
});
